import { Modal, ModalProps } from '@billy/design-system'
import { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { useVatDeclaration } from '@features/vatDeclarations/hooks/useVatDeclaration'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { shouldUseDKReportContent } from '../../utils/isSupportedCountryId'
import { VatDeclarationsSettlePeriodModalContextProvider } from './context/vatDeclarationsSettlePeriodModalContext'
import { ModalBodyDE, ModalBodyDK, ModalBodyFR, ModalBodyNL } from './elements/ModalBody'
import { ModalFooter, ModalFooterNL } from './elements/ModalFooter'

interface CountryComponents {
  body: ReactElement
  footer: ReactElement
  titleKey: string
}

const countryComponents: Record<string, CountryComponents> = {
  DK: {
    body: <ModalBodyDK />,
    footer: <ModalFooter />,
    titleKey: 'vat_declarations.settlement_modal.title',
  },
  DE: {
    body: <ModalBodyDE />,
    footer: <ModalFooter />,
    titleKey: 'vat_declarations.settlement_modal.title',
  },
  FR: {
    body: <ModalBodyFR />,
    footer: <ModalFooter />,
    titleKey: 'vat_declarations.settlement_modal.title',
  },
  NL: {
    body: <ModalBodyNL />,
    footer: <ModalFooterNL />,
    titleKey: 'vat_declarations.submit_manually',
  },
}

interface VatDeclarationsSettleModalProps extends ModalProps {
  vatDeclarationId: string
}

export const VatDeclarationsSettlePeriodModal = ({
  vatDeclarationId,
  id,
  ...rest
}: VatDeclarationsSettleModalProps): ReactElement => {
  const { t } = useTranslation()
  const { vatDeclaration } = useVatDeclaration({ vatDeclarationId })
  const { organization } = useUserOrganization()

  const periodText = vatDeclaration?.periodText

  if (!vatDeclaration) {
    return <></>
  }

  const countryId = organization?.countryId || 'FR'

  const { body, footer, titleKey } = shouldUseDKReportContent(countryId)
    ? countryComponents.DK
    : countryComponents[countryId]

  return (
    <VatDeclarationsSettlePeriodModalContextProvider modalId={id} vatDeclaration={vatDeclaration}>
      <Modal id={id} {...rest} closable size="s">
        <Modal.Header title={t(titleKey, { period: periodText })} />
        <Modal.Body>{body}</Modal.Body>
        <Modal.Footer>{footer}</Modal.Footer>
      </Modal>
    </VatDeclarationsSettlePeriodModalContextProvider>
  )
}
